import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, Modal, ConfirmDialog, FormField, TextInput, TextArea, FormSelect,
  StatusBadge, PageHeader, DataTable, FilterBar, Tabs, EmptyState, Toggle,
  type DataTableColumn,
} from '@/components/ui';
import {
  superApi,
  type VoiceTool, type VoiceToolMethod, type VoiceToolParam, type VoiceToolParamSource,
  type VoiceToolResponseMapping, type VoiceToolTestResult, type VoiceSecret,
} from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface TenantLite { id: number; name: string; slug: string }

const METHODS: VoiceToolMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

// ── Page ────────────────────────────────────────────────────────────────────
export function VoiceTools() {
  const [tab, setTab] = useState('tools');
  const [tenants, setTenants] = useState<TenantLite[]>([]);

  // בורר מרכזיות נטען פעם אחת ומשותף בין לשונית הכלים ללשונית הסודות.
  useEffect(() => {
    void (async () => {
      try {
        const t = await superApi.tenants({ per_page: 500 });
        const items = (t as { data?: unknown[] }).data ?? [];
        setTenants(items.map((x) => x as TenantLite).filter((x) => x && typeof x.id === 'number'));
      } catch { /* non-fatal */ }
    })();
  }, []);

  const tenantName = useCallback(
    (id?: number | null) => (id == null ? null : tenants.find((t) => t.id === id)?.name ?? `#${id}`),
    [tenants],
  );

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader
        title="כלי API קוליים"
        subtitle="ספריית כלים שסוכני קול קוראים להם תוך כדי שיחה (API חיצוני), וסודות מוצפנים"
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { id: 'tools', label: 'כלים' },
          { id: 'secrets', label: 'סודות' },
        ]}
      />
      {tab === 'tools' && <ToolsTab tenants={tenants} tenantName={tenantName} />}
      {tab === 'secrets' && <SecretsTab tenants={tenants} tenantName={tenantName} />}
    </div>
  );
}

// ── Tools library ─────────────────────────────────────────────────────────────
function ToolsTab({ tenants, tenantName }: { tenants: TenantLite[]; tenantName: (id?: number | null) => string | null }) {
  const [rows, setRows] = useState<VoiceTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<VoiceTool | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VoiceTool | null>(null);
  const [testTarget, setTestTarget] = useState<VoiceTool | null>(null);

  const reqSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    setLoading(true);
    try {
      const res = await superApi.voiceToolsLibrary({ search: search || undefined, per_page: 100 });
      if (seq !== reqSeq.current) return;
      setRows(res.data);
    } catch {
      if (seq === reqSeq.current) toast('שגיאה בטעינת הכלים', 'danger');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const h = setTimeout(() => void load(), 250);
    return () => clearTimeout(h);
  }, [load]);

  const columns: DataTableColumn<VoiceTool>[] = useMemo(() => [
    { key: 'name', he: 'שם', render: (r) => <strong dir="ltr">{r.name}</strong> },
    {
      key: 'endpoint', he: 'קריאה', align: 'start',
      render: (r) => (
        <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <StatusBadge tone="neutral" size="sm">{r.method}</StatusBadge>
          <span style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.url}</span>
        </span>
      ),
    },
    {
      key: 'scope', he: 'שיוך', render: (r) =>
        r.tenant_id
          ? <StatusBadge tone="neutral" size="sm">מרכזיה · {tenantName(r.tenant_id)}</StatusBadge>
          : <StatusBadge tone="ok" size="sm">גלובלי</StatusBadge>,
    },
    {
      key: 'agents_count', he: 'בשימוש', align: 'center',
      render: (r) => (r.agents_count
        ? <StatusBadge tone="info" size="sm">{r.agents_count} סוכנים</StatusBadge>
        : <span style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>—</span>),
    },
    {
      key: 'updated_at', he: 'עודכן', align: 'start',
      render: (r) => <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.updated_at ?? '—'}</span>,
    },
    {
      key: 'actions', he: '', align: 'center',
      render: (r) => (
        <button
          type="button"
          className="btn"
          style={{ height: 30 }}
          onClick={(e) => { e.stopPropagation(); setTestTarget(r); }}
        >
          בדיקה
        </button>
      ),
    },
  ], [tenantName]);

  async function handleDelete(tool: VoiceTool) {
    try {
      await superApi.deleteVoiceTool(tool.id);
      toast('הכלי נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== tool.id));
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה במחיקה';
      toast(msg, 'danger');
    }
  }

  return (
    <div className="stack">
      <FilterBar
        search={search}
        onSearch={setSearch}
        onClear={() => setSearch('')}
        placeholder="חיפוש לפי שם או כתובת…"
      >
        <button type="button" className="btn primary" style={{ height: 36 }} onClick={() => setCreateOpen(true)}>
          + כלי חדש
        </button>
      </FilterBar>

      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          onRowClick={(r) => setEditTarget(r)}
          caption="כלי API קוליים"
          emptyState={
            <EmptyState
              title="אין כלים עדיין"
              description="צור כלי ראשון כדי שסוכני קול יוכלו לקרוא ל-API חיצוני תוך כדי שיחה."
              action={<button type="button" className="btn primary" onClick={() => setCreateOpen(true)}>+ כלי חדש</button>}
            />
          }
        />
      </Card>

      {createOpen && (
        <VoiceToolForm
          mode="create"
          tenants={tenants}
          onClose={() => setCreateOpen(false)}
          onSaved={() => { setCreateOpen(false); void load(); }}
        />
      )}
      {editTarget && (
        <VoiceToolForm
          mode="edit"
          tool={editTarget}
          tenants={tenants}
          onClose={() => setEditTarget(null)}
          onDelete={() => { setDeleteTarget(editTarget); setEditTarget(null); }}
          onTest={() => { setTestTarget(editTarget); setEditTarget(null); }}
          onSaved={() => { setEditTarget(null); void load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="מחיקת כלי"
        message={deleteTarget ? `למחוק את "${deleteTarget.name}"? סוכנים שמקושרים אליו יאבדו את הכלי.` : ''}
        confirmLabel="מחק"
      />

      {testTarget && (
        <VoiceToolTestModal tool={testTarget} onClose={() => setTestTarget(null)} />
      )}
    </div>
  );
}

// ── Create / edit form ───────────────────────────────────────────────────────
interface HeaderRow { key: string; value: string }

export function VoiceToolForm({
  mode, tool, tenants, onClose, onSaved, onDelete, onTest,
}: {
  mode: 'create' | 'edit';
  tool?: VoiceTool;
  tenants: TenantLite[];
  onClose: () => void;
  onSaved: (saved?: VoiceTool) => void;
  onDelete?: () => void;
  onTest?: () => void;
}) {
  const [form, setForm] = useState({
    name: tool?.name ?? '',
    tenant_id: tool?.tenant_id != null ? String(tool.tenant_id) : '',
    description: tool?.description ?? '',
    method: (tool?.method ?? 'GET') as VoiceToolMethod,
    url: tool?.url ?? '',
    timeout_ms: tool?.timeout_ms != null ? String(tool.timeout_ms) : '',
    speak_while_waiting: tool?.speak_while_waiting ?? '',
    on_error_instruction: tool?.on_error_instruction ?? '',
    return_mapped_only: tool?.return_mapped_only ?? false,
  });
  const [headers, setHeaders] = useState<HeaderRow[]>(
    tool?.headers ? Object.entries(tool.headers).map(([key, value]) => ({ key, value })) : [],
  );
  const [params, setParams] = useState<VoiceToolParam[]>(tool?.params ?? []);
  const [mappings, setMappings] = useState<VoiceToolResponseMapping[]>(tool?.response_mappings ?? []);
  const [saving, setSaving] = useState(false);

  const nameValid = /^[a-z][a-z0-9_]*$/.test(form.name.trim());

  function buildPayload(): Partial<VoiceTool> {
    const headerObj = headers.reduce<Record<string, string>>((acc, h) => {
      if (h.key.trim()) acc[h.key.trim()] = h.value;
      return acc;
    }, {});
    const cleanParams = params
      .filter((p) => p.name?.trim())
      .map((p) => {
        const base: VoiceToolParam = { name: p.name.trim(), in: p.in, source: p.source };
        if (p.source === 'static') base.value = p.value ?? '';
        if (p.source === 'variable') base.value = p.value ?? '';
        if (p.source === 'from_model') {
          base.type = p.type ?? 'string';
          base.description = p.description?.trim() || null;
          base.required = !!p.required;
          const en = (p.enum ?? []).map((v) => v.trim()).filter(Boolean);
          if (en.length) base.enum = en;
        }
        return base;
      });
    const cleanMappings = mappings.filter((m) => m.path?.trim() && m.variable?.trim())
      .map((m) => ({ path: m.path.trim(), variable: m.variable.trim() }));
    return {
      name: form.name.trim(),
      tenant_id: form.tenant_id ? Number(form.tenant_id) : null,
      description: form.description.trim() || null,
      method: form.method,
      url: form.url.trim(),
      headers: Object.keys(headerObj).length ? headerObj : null,
      params: cleanParams.length ? cleanParams : [],
      timeout_ms: form.timeout_ms ? Number(form.timeout_ms) : null,
      speak_while_waiting: form.speak_while_waiting.trim() || null,
      on_error_instruction: form.on_error_instruction.trim() || null,
      response_mappings: cleanMappings.length ? cleanMappings : [],
      return_mapped_only: form.return_mapped_only,
    };
  }

  async function save() {
    if (!form.name.trim()) { toast('יש למלא שם', 'danger'); return; }
    if (!nameValid) { toast('שם חייב להיות snake_case (אותיות קטנות, מספרים וקו תחתון)', 'danger'); return; }
    if (!form.url.trim()) { toast('יש למלא כתובת URL', 'danger'); return; }
    setSaving(true);
    try {
      let saved: VoiceTool | undefined;
      if (mode === 'create') {
        saved = await superApi.createVoiceTool(buildPayload());
        toast('הכלי נוצר', 'ok');
      } else if (tool) {
        saved = await superApi.updateVoiceTool(tool.id, buildPayload());
        toast('עודכן', 'ok');
      }
      onSaved(saved);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={mode === 'create' ? 'כלי API חדש' : `עריכת ${tool?.name}`}
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {mode === 'edit' && onTest && (
              <button type="button" className="btn" onClick={onTest} disabled={saving}>בדיקה</button>
            )}
            {mode === 'edit' && onDelete && (
              <button type="button" className="btn danger" onClick={onDelete} disabled={saving}>מחק</button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="button" className="btn primary" onClick={save} disabled={saving}>
              {saving ? 'שומר…' : 'שמור'}
            </button>
          </div>
        </div>
      }
    >
      <div className="stack">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="שם" required hint="snake_case — כך המודל קורא לכלי (למשל check_package)" error={form.name && !nameValid ? 'רק אותיות קטנות, מספרים וקו תחתון' : undefined}>
            <TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} dir="ltr" placeholder="check_package" />
          </FormField>
          <FormField label="שיוך" hint="ריק = כלי גלובלי הזמין לכל המרכזיות">
            <FormSelect value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
              <option value="">גלובלי (משותף)</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </FormSelect>
          </FormField>
        </div>

        <FormField label="תיאור" hint='טקסט זה נקרא ע"י המודל — נסח מתי להשתמש ומה חוזר'>
          <TextArea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </FormField>

        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 'var(--gap)' }}>
          <FormField label="שיטה">
            <FormSelect value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value as VoiceToolMethod })}>
              {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            </FormSelect>
          </FormField>
          <FormField label="כתובת URL" required hint="רק https לכתובת ציבורית. אפשר {{var}} או {{secret.key}}">
            <TextInput value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} dir="ltr" placeholder="https://api.example.com/orders/{{tracking}}" />
          </FormField>
        </div>

        {/* ── Headers ── */}
        <Section title="כותרות (Headers)" hint="ערכים יכולים להכיל {{var}} או {{secret.key}} — למשל x-api-key: {{secret.example_key}}">
          {headers.map((h, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr auto', gap: 8 }}>
              <TextInput placeholder="שם" value={h.key} dir="ltr"
                onChange={(e) => setHeaders((p) => p.map((x, j) => (j === i ? { ...x, key: e.target.value } : x)))} />
              <TextInput placeholder="ערך" value={h.value} dir="ltr"
                onChange={(e) => setHeaders((p) => p.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} />
              <button type="button" className="btn danger" onClick={() => setHeaders((p) => p.filter((_, j) => j !== i))}>הסר</button>
            </div>
          ))}
          <div>
            <button type="button" className="btn" onClick={() => setHeaders((p) => [...p, { key: '', value: '' }])}>+ כותרת</button>
          </div>
        </Section>

        {/* ── Params ── */}
        <Section title="פרמטרים" hint="static = ערך קבוע · variable = משתנה שנצבר בשיחה · from_model = המודל ממלא">
          {params.map((p, i) => {
            const patch = (v: Partial<VoiceToolParam>) => setParams((prev) => prev.map((x, j) => (j === i ? { ...x, ...v } : x)));
            return (
              <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-2)', padding: 10, display: 'grid', gap: 6 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 100px 130px auto', gap: 8, alignItems: 'center' }}>
                  <TextInput placeholder="שם הפרמטר" value={p.name} dir="ltr" onChange={(e) => patch({ name: e.target.value })} />
                  <FormSelect value={p.in} onChange={(e) => patch({ in: e.target.value as VoiceToolParam['in'] })}>
                    <option value="query">query</option>
                    <option value="body">body</option>
                  </FormSelect>
                  <FormSelect value={p.source} onChange={(e) => patch({ source: e.target.value as VoiceToolParamSource })}>
                    <option value="static">קבוע</option>
                    <option value="variable">משתנה</option>
                    <option value="from_model">מהמודל</option>
                  </FormSelect>
                  <button type="button" className="btn danger" onClick={() => setParams((prev) => prev.filter((_, j) => j !== i))}>הסר</button>
                </div>
                {p.source === 'static' && (
                  <TextInput placeholder="ערך קבוע" value={p.value ?? ''} dir="ltr" onChange={(e) => patch({ value: e.target.value })} />
                )}
                {p.source === 'variable' && (
                  <TextInput placeholder="שם משתנה (למשל tracking_number)" value={p.value ?? ''} dir="ltr" onChange={(e) => patch({ value: e.target.value })} />
                )}
                {p.source === 'from_model' && (
                  <div style={{ display: 'grid', gap: 6 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: 8, alignItems: 'center' }}>
                      <FormSelect value={p.type ?? 'string'} onChange={(e) => patch({ type: e.target.value as VoiceToolParam['type'] })}>
                        <option value="string">string</option>
                        <option value="number">number</option>
                        <option value="integer">integer</option>
                        <option value="boolean">boolean</option>
                      </FormSelect>
                      <TextInput placeholder="תיאור למודל — מה למלא" value={p.description ?? ''} onChange={(e) => patch({ description: e.target.value })} />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-sm)', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={!!p.required} onChange={(e) => patch({ required: e.target.checked })} />
                        חובה
                      </label>
                    </div>
                    <TextInput placeholder="ערכים אפשריים (enum) מופרדים בפסיק — אופציונלי" dir="ltr"
                      value={(p.enum ?? []).join(', ')}
                      onChange={(e) => patch({ enum: e.target.value.split(',').map((v) => v.trim()).filter(Boolean) })} />
                  </div>
                )}
              </div>
            );
          })}
          <div>
            <button type="button" className="btn" onClick={() => setParams((prev) => [...prev, { name: '', in: 'query', source: 'from_model', type: 'string', required: false }])}>+ פרמטר</button>
          </div>
        </Section>

        {/* ── Behaviour ── */}
        <Section title="התנהגות בשיחה">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
            <FormField label="timeout (מ״ש)" hint="ריק = ברירת מחדל">
              <TextInput type="number" value={form.timeout_ms} onChange={(e) => setForm({ ...form, timeout_ms: e.target.value })} dir="ltr" placeholder="8000" />
            </FormField>
            <FormField label="משפט המתנה" hint="מה הסוכן אומר בזמן שהקריאה רצה">
              <TextInput value={form.speak_while_waiting} onChange={(e) => setForm({ ...form, speak_while_waiting: e.target.value })} placeholder="רק רגע, בודק עבורך…" />
            </FormField>
          </div>
          <FormField label="הנחיה בשגיאה" hint="מה הסוכן יגיד למתקשר אם הקריאה נכשלת">
            <TextInput value={form.on_error_instruction} onChange={(e) => setForm({ ...form, on_error_instruction: e.target.value })} placeholder="לא הצלחתי לאתר את הפרטים כרגע, אפשר לנסות שוב מאוחר יותר." />
          </FormField>
        </Section>

        {/* ── Response mappings ── */}
        <Section title="מיפוי תשובה" hint="חילוץ ערכים מתשובת ה-API למשתני שיחה — path (JSONPath/dot) → שם משתנה">
          {mappings.map((m, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8 }}>
              <TextInput placeholder="data.status" value={m.path} dir="ltr"
                onChange={(e) => setMappings((p) => p.map((x, j) => (j === i ? { ...x, path: e.target.value } : x)))} />
              <TextInput placeholder="שם משתנה" value={m.variable} dir="ltr"
                onChange={(e) => setMappings((p) => p.map((x, j) => (j === i ? { ...x, variable: e.target.value } : x)))} />
              <button type="button" className="btn danger" onClick={() => setMappings((p) => p.filter((_, j) => j !== i))}>הסר</button>
            </div>
          ))}
          <div>
            <button type="button" className="btn" onClick={() => setMappings((p) => [...p, { path: '', variable: '' }])}>+ מיפוי</button>
          </div>
          <Toggle
            checked={form.return_mapped_only}
            onChange={(v) => setForm({ ...form, return_mapped_only: v })}
            label="החזר למודל רק את המשתנים הממופים (ולא את כל התשובה)"
          />
        </Section>
      </div>
    </Modal>
  );
}

// ── Test modal ────────────────────────────────────────────────────────────────
function VoiceToolTestModal({ tool, onClose }: { tool: VoiceTool; onClose: () => void }) {
  const [sampleText, setSampleText] = useState('{}');
  const [argsText, setArgsText] = useState('{}');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<VoiceToolTestResult | null>(null);
  const [jsonErr, setJsonErr] = useState<string | null>(null);

  async function run() {
    let sample: Record<string, unknown> = {};
    let modelArgs: Record<string, unknown> = {};
    try {
      sample = sampleText.trim() ? JSON.parse(sampleText) : {};
      modelArgs = argsText.trim() ? JSON.parse(argsText) : {};
      setJsonErr(null);
    } catch {
      setJsonErr('JSON לא תקין באחד השדות');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const res = await superApi.testVoiceTool(tool.id, { sample_variables: sample, model_arguments: modelArgs });
      setResult(res);
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בהרצת הבדיקה';
      toast(msg, 'danger');
    } finally {
      setRunning(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={`בדיקת כלי — ${tool.name}`}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={running}>סגור</button>
          <button type="button" className="btn primary" onClick={run} disabled={running}>
            {running ? 'מריץ…' : 'הרץ בדיקה'}
          </button>
        </>
      }
    >
      <div className="stack">
        <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
          הבדיקה מבצעת קריאת HTTP אמיתית ל-API. הזן ערכי דוגמה למשתני שיחה (sample_variables)
          ולפרמטרים שהמודל ממלא (model_arguments).
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap)' }}>
          <FormField label="משתני שיחה (sample_variables)">
            <TextArea rows={4} value={sampleText} dir="ltr" onChange={(e) => setSampleText(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }} />
          </FormField>
          <FormField label="ערכי המודל (model_arguments)">
            <TextArea rows={4} value={argsText} dir="ltr" onChange={(e) => setArgsText(e.target.value)} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)' }} />
          </FormField>
        </div>
        {jsonErr && <div style={{ color: 'var(--danger)', fontSize: 'var(--t-xs)' }}>{jsonErr}</div>}

        {result && (
          <div className="stack" style={{ borderTop: '1px solid var(--line)', paddingTop: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <StatusBadge tone={result.ok ? 'ok' : 'danger'} dot size="sm">
                {result.ok ? 'הצליח' : 'נכשל'}
              </StatusBadge>
              {result.status != null && <span dir="ltr" style={{ fontSize: 'var(--t-sm)' }}>HTTP {result.status}</span>}
              {result.duration_ms != null && <span dir="ltr" style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{result.duration_ms}ms</span>}
            </div>
            {result.error && <div style={{ color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>{result.error}</div>}

            <JsonBlock label="בקשה שנשלחה" value={result.request} />
            {result.mapped_variables && Object.keys(result.mapped_variables).length > 0 && (
              <JsonBlock label="משתנים ממופים" value={result.mapped_variables} tone="ok" />
            )}
            <JsonBlock label="תשובת ה-API" value={result.response} />
          </div>
        )}
      </div>
    </Modal>
  );
}

function JsonBlock({ label, value, tone }: { label: string; value: unknown; tone?: 'ok' }) {
  return (
    <div>
      <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>{label}</div>
      <pre
        dir="ltr"
        style={{
          margin: 0, padding: 10, borderRadius: 'var(--r-2)', overflow: 'auto', maxHeight: 260,
          background: 'var(--surface-2)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-xs)',
          border: `1px solid ${tone === 'ok' ? 'var(--accent)' : 'var(--line)'}`,
        }}
      >
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

// ── Secrets ───────────────────────────────────────────────────────────────────
function SecretsTab({ tenants, tenantName }: { tenants: TenantLite[]; tenantName: (id?: number | null) => string | null }) {
  const [rows, setRows] = useState<VoiceSecret[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterTenant, setFilterTenant] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<VoiceSecret | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await superApi.voiceSecrets(filterTenant ? { tenant_id: Number(filterTenant) } : undefined);
      setRows(res);
    } catch {
      toast('שגיאה בטעינת הסודות', 'danger');
    } finally {
      setLoading(false);
    }
  }, [filterTenant]);

  useEffect(() => { void load(); }, [load]);

  const columns: DataTableColumn<VoiceSecret>[] = useMemo(() => [
    { key: 'key', he: 'מפתח', render: (r) => <strong dir="ltr">{r.key}</strong> },
    { key: 'value', he: 'ערך', render: () => <span style={{ letterSpacing: 2, color: 'var(--muted)' }}>••••••••</span> },
    {
      key: 'scope', he: 'שיוך', render: (r) =>
        r.tenant_id
          ? <StatusBadge tone="neutral" size="sm">מרכזיה · {tenantName(r.tenant_id)}</StatusBadge>
          : <StatusBadge tone="ok" size="sm">גלובלי</StatusBadge>,
    },
    {
      key: 'created_at', he: 'נוצר', align: 'start',
      render: (r) => <span dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{r.created_at ?? '—'}</span>,
    },
    {
      key: 'actions', he: '', align: 'center',
      render: (r) => (
        <button type="button" className="btn danger" style={{ height: 30 }} onClick={(e) => { e.stopPropagation(); setDeleteTarget(r); }}>
          מחק
        </button>
      ),
    },
  ], [tenantName]);

  async function handleDelete(secret: VoiceSecret) {
    try {
      await superApi.deleteVoiceSecret(secret.id);
      toast('הסוד נמחק', 'ok');
      setDeleteTarget(null);
      setRows((prev) => prev.filter((r) => r.id !== secret.id));
    } catch {
      toast('שגיאה במחיקה', 'danger');
    }
  }

  return (
    <div className="stack">
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: 0 }}>
        סודות משמשים בכלים דרך {'{{secret.key}}'} בכותרות או בכתובת. הערך נשמר מוצפן ולא ניתן לצפייה לאחר שמירה.
      </p>
      <div style={{ display: 'flex', gap: 'var(--gap)', alignItems: 'center', justifyContent: 'space-between' }}>
        <FormSelect value={filterTenant} onChange={(e) => setFilterTenant(e.target.value)} style={{ width: 220, height: 36 }}>
          <option value="">כל השיוכים</option>
          {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </FormSelect>
        <button type="button" className="btn primary" style={{ height: 36 }} onClick={() => setAddOpen(true)}>
          + סוד חדש
        </button>
      </div>

      <Card>
        <DataTable
          columns={columns}
          rows={rows}
          loading={loading}
          caption="סודות קוליים"
          emptyState={
            <EmptyState
              title="אין סודות עדיין"
              description="הוסף מפתח וערך כדי לאחסן מפתחות API בצורה מוצפנת."
              action={<button type="button" className="btn primary" onClick={() => setAddOpen(true)}>+ סוד חדש</button>}
            />
          }
        />
      </Card>

      {addOpen && (
        <SecretForm
          tenants={tenants}
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); void load(); }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && handleDelete(deleteTarget)}
        title="מחיקת סוד"
        message={deleteTarget ? `למחוק את הסוד "${deleteTarget.key}"? כלים שמשתמשים בו יפסיקו לעבוד.` : ''}
        confirmLabel="מחק"
      />
    </div>
  );
}

function SecretForm({ tenants, onClose, onSaved }: { tenants: TenantLite[]; onClose: () => void; onSaved: () => void }) {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!key.trim()) { toast('יש למלא מפתח', 'danger'); return; }
    if (!value.trim()) { toast('יש למלא ערך', 'danger'); return; }
    setSaving(true);
    try {
      await superApi.saveVoiceSecret({
        tenant_id: tenantId ? Number(tenantId) : null,
        key: key.trim(),
        value,
      });
      toast('הסוד נשמר', 'ok');
      onSaved();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'שגיאה בשמירה';
      toast(msg, 'danger');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="sm"
      title="סוד חדש"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={saving}>ביטול</button>
          <button type="button" className="btn primary" onClick={save} disabled={saving}>
            {saving ? 'שומר…' : 'שמור'}
          </button>
        </>
      }
    >
      <div className="stack">
        <FormField label="מפתח" required hint="כך מפנים אליו: {{secret.key}}">
          <TextInput value={key} onChange={(e) => setKey(e.target.value)} dir="ltr" placeholder="example_api_key" />
        </FormField>
        <FormField label="ערך" required hint="הערך נשמר מוצפן ולא ניתן לצפייה לאחר שמירה">
          <TextInput type="password" value={value} onChange={(e) => setValue(e.target.value)} dir="ltr" />
        </FormField>
        <FormField label="שיוך" hint="ריק = סוד גלובלי לכל המרכזיות. עדכון מפתח קיים דורס את הערך.">
          <FormSelect value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">גלובלי (משותף)</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </FormSelect>
        </FormField>
      </div>
    </Modal>
  );
}

// ── Small section wrapper ─────────────────────────────────────────────────────
function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 4 }}>
        <strong style={{ fontSize: 'var(--t-sm)' }}>{title}</strong>
        {hint && <p style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)', margin: '4px 0 0' }}>{hint}</p>}
      </div>
      {children}
    </div>
  );
}
